import React, { useEffect, useRef, useState } from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet-updated';
import Header from "./common/Header";
import './Cart.css';
import { Link } from "react-router-dom";
import httpService from '../services/HttpService'; // Import your HttpService (adjust path as needed)
import {
    formattedPrice,
    plural,
    calculateSaleFromCartItems,
    calculateTotalFromCartItems,
    calculateFinalFromCartItems
} from '../utils/priceFormatter';
import useIsMobile from '../hooks/useIsMobile';
import { ReactComponent as Promo } from '../assets/icons/promocode.svg';
import { ReactComponent as Question } from '../assets/icons/question.svg';
import { Modal } from './common/Modal';

const Cart = () => {
    const [cartItems, setCartItems] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [notification, setNotification] = useState(null);
    const effectRan = useRef(false);
    const [alertedProducts, setAlertedProducts] = useState(new Set());
    const isMobile = useIsMobile();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [code, setCode] = useState('');
    const [errorCode, setErrorCode] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [appliedCode, setAppliedCode] = useState(false);
    const [openModalInfo, setOpenModalInfo] = useState(false);
    const inputRef = useRef(null);
    const headerRef = useRef();


    const [isOpenModal, setIsOpenModal]= useState(false)
    const currentItemId = useRef(null)
    // Fetch cart data
    useEffect(() => {
        if (effectRan.current) return;
        effectRan.current = true;
        const fetchCart = async () => {
            try {
                const response = await httpService.get('/cart'); // Returns Response object
                if (!response.ok) {
                    throw new Error(`Error fetching cart: ${response.statusText}`);
                }
                let data = await response.json(); // Manually parse JSON
                data = data.map((i) => {
                    i.availability = i.product.stocks.find((stock) => stock.size === i.size)?.count;
                    return i;
                })
                setCartItems(data);
                calculateTotalPrice(data);
                setAlertedItems();
            } catch (error) {
                console.error('Error fetching cart:', error);
            }
        };
        const fetchCurrentCode = async () => {
            try {
                const response = await httpService.get('/promo-codes/current'); // Returns Response object
                if (!response.ok) {
                    throw new Error(`${response.statusText}`);
                }
                let data = await response.json();
                if (data.code) setAppliedCode(data)

            } catch (error) {
                console.error(error);
            }
        };
        fetchCart();
        fetchCurrentCode();

    }, []);
    const handleChangeCode = (e) => {
        setCode(e.target.value)
        setErrorCode(false)
    }
    useEffect(() => {
        headerRef.current?.updateCartCount(cartItems.length);
        if (effectRan.current && !cartItems.length) return;
        effectRan.current = true;
        setAlertedItems();
    }, [cartItems]);

    const setAlertedItems = () => {
        const initialAlerted = new Set(
            cartItems.filter((p) => p.alert_count > 0).map((p) => p.product_id)
        );

        setAlertedProducts(initialAlerted);
    }

    const handleRestock = async (productId, size) => {
        try {
            const isAlerted = alertedProducts.has(productId);
            const endpoint = "/restock-alerts";
            const method = isAlerted ? "delete" : "post";
            const payload = { product_id: productId, size };

            const response = await httpService[method](endpoint, payload);
            const res = await response.json();

            if ((isAlerted && res?.message) || (!isAlerted && res?.id)) {
                setAlertedProducts(prev => {
                    const updated = new Set(prev);
                    isAlerted ? updated.delete(productId) : updated.add(productId);
                    return updated;
                });

                setNotification(isAlerted ? "Оповещение удалено" : "Оповещение о пополнении отправлено");
            }
        } catch (error) {
            console.error("Error:", error);
            setNotification("Ошибка");
        }

        setTimeout(() => setNotification(null), 1500);
    };

    // Calculate total price based on all cart items
    const calculateTotalPrice = (items) => {
        const total = items.reduce((sum, item) => {
            return item.availability === 0 ? sum : sum + item.product.actual_price * item.count;
        }, 0);
        setTotalPrice(total);  // Update the total price in state
    };

    // Send a PUT request to update cart item
    const updateCartItem = async (cartId, size, count) => {
        try {
            const response = await httpService.put(`/cart/${cartId}`, { size, count }); // Returns Response object
            if (!response.ok) {
                throw new Error(`Failed to update cart item: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error updating cart item:', error);
        }
    };

    // Handle removing item from cart
    const handleRemoveItem = async (cartId) => {
        try {
            const response = await httpService.delete(`/cart/${cartId}`); // Returns Response object
            if (!response.ok) {
                throw new Error(`Error removing item: ${response.statusText}`);
            }
            // Remove item from state
            const updatedItems = cartItems.filter(item => item.id !== cartId);
            setCartItems(updatedItems);
            calculateTotalPrice(updatedItems);  // Recalculate total price after item removal
        } catch (error) {
            console.error('Error removing item:', error);
        }
        currentItemId.current = null;
        setIsOpenModal(false);
    };
    const handleClickRemove = (cartId) => {
        currentItemId.current = cartId
        setIsOpenModal(true)
    };
    const authToken = localStorage.getItem('auth_token');
    // Handle size change
    const handleSizeChange = async (cartId, newSize) => {
        const updatedItems = cartItems.map(item => {
            if (item.id === cartId) {
                return { ...item, size: newSize };
            }
            return item;
        });
        setCartItems(updatedItems);
        const item = updatedItems.find(item => item.id === cartId);
        await updateCartItem(cartId, newSize, item.count); // Send updated size and count to backend
    };

    const handleCountChange = async (cartId, newCount) => {
        const updatedItems = cartItems.map(item => {
            if (item.id === cartId) {
                return { ...item, count: newCount };
            }
            return item;
        });
        setCartItems(updatedItems);
        calculateTotalPrice(updatedItems);  // Recalculate total price after count change
        const item = updatedItems.find(item => item.id === cartId);
        await updateCartItem(cartId, item.size, item.count); // Send updated size and count to backend
    };

    const handleOrder = async () => {
        const identityToken = "aaa"; // Assuming the token is stored in localStorage; replace with dynamic value if needed
        const cartItemIds = cartItems.map(item => item.id); // Collect all cart item IDs

        try {
            const response = await httpService.post('/orders', {
                cart_item_ids: cartItemIds,
            }); // Returns Response object
            if (!response.ok) {
                throw new Error(`Failed to create order: ${response.statusText}`);
            }
            const data = await response.json(); // Manually parse JSON for order data
            localStorage.setItem('pending_order_id', data.id);

            // Redirect or show success message
            // For example, navigate to a success page (you may need to use useNavigate from react-router-dom)
        } catch (error) {
            console.error('Error creating order:', error);
            alert('Ошибка при создании заказа. Попробуйте снова.');
        }
    };


    const handleInfoCodeClick = (e) => {
        e.stopPropagation();
        setOpenModalInfo(true)
    };

    const checkCode = async () => {
        try {
            const response = await httpService.post(`/promo-codes/`, { code: code })
            const data = await response.json();

            if(data.success){
                setAppliedCode(data.promo_code)
                setErrorMessage(null)
                setTimeout(() => {
                    setDropdownOpen(false)
                }, 900)
            }else{
                setErrorCode(true)
                setErrorMessage(data?.message)
            }

        } catch (error) {
            console.error(error);
            setErrorCode(true)
        }
    };
    const handleClickCleanInput = () => {
        setCode('');
        setErrorCode(false);
        setErrorMessage(null);
    }
    const handleDeleteCode = async () => {
        try {
            const response = await httpService.delete(`/promo-codes/${appliedCode.id}`)
            if (!response.ok) {
                throw new Error(`${response.statusText}`);
            }
            setOpenModalInfo(false)
            setAppliedCode(null);
            setCode('')
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <>
            <Header ref={headerRef}/>
            <main className="cart-main">
                <div className="cart-container">
                    <div className="cart-title">Корзина</div>

                    {cartItems.length === 0 ? (
                        <p>Ваша корзина пуста</p>
                    ) : (
                        <div className="cart-items-list">
                            {cartItems.map((item) => (
                                <div key={item.id} className="cart-item">
                                    <div className="cart-item-left">
                                        <Link className="link" to={`/product/${item.product.id}`}>
                                            <img
                                                src={item.product.main_image.link}
                                                alt={item.product.name}
                                                className="cart-item-image"
                                            />
                                        </Link>
                                    </div>
                                    <div className="cart-item-middle">
                                        <p className="cart-item-brand-name">{item.product.brand.name}</p>
                                        <p className="cart-item-name">{item.product.name}</p>
                                        <p className="cart-item-size">Size: {item.size}</p>
                                        {item.availability === 0 && <p className="cart-item-availability margin-15">Нет в наличии</p>}
                                        {item.availability === 1 && <p className="cart-item-availability">Последний в наличии!</p>}
                                        {item.availability === 0 ? (
                                            <button
                                                className={`restock-button ${alertedProducts.has(item.product_id) ? "active" : ""}`}
                                                onClick={() => handleRestock(item.product_id, item.size)}
                                            >
                                                Сообщить о поступлении
                                                <span className="material-symbols-sharp">notifications</span>
                                            </button>
                                        ) : (
                                            <div className="cart-item-options">
                                                <button
                                                    className="count-button decrement"
                                                    onClick={() => handleCountChange(item.id, Math.max(1, item.count - 1))}
                                                >
                                                    −
                                                </button>
                                                <select
                                                    className="count-select"
                                                    value={item.count}
                                                    onChange={(e) => handleCountChange(item.id, parseInt(e.target.value))}
                                                >
                                                    {Array.from(
                                                        { length: item.product.stocks.find((stock) => stock.size === item.size)?.count || item.product.stock },
                                                        (_, i) => i + 1
                                                    ).map((count) => (
                                                        <option key={count} value={count}>
                                                            {count}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    className="count-button increment"
                                                    onClick={() =>
                                                        handleCountChange(
                                                            item.id,
                                                            Math.min(
                                                                item.product.stocks.find((stock) => stock.size === item.size)?.count || item.product.stock,
                                                                item.count + 1
                                                            )
                                                        )
                                                    }
                                                >
                                                    +
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="cart-item-right">
                                        <span
                                            onClick={() => {
                                                // const confirmed = window.confirm('Удалить товар из корзины?');
                                                // if (confirmed) {
                                                    handleClickRemove(item.id);
                                                // }
                                            }}
                                            className="material-symbols-sharp remove-button"
                                        >
                                            close
                                        </span>
                                        {item.availability !== 0 && <p className="cart-item-price">{formattedPrice(item.product.price * item.count, null, item.product.sale)} <br />{formattedPrice(null, item.product.actual_price * item.count, null)}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {isMobile ? (
                    <div className="cart-total-mobile">
                        <div className='cart-total-summary'>
                            <div>{cartItems.length} {plural(cartItems.length, ['товар', 'товара', 'товаров'])}</div>
                            <div>
                                {calculateSaleFromCartItems(cartItems, appliedCode) && (<>
                                <span style={{ textDecoration: "line-through" }}>{calculateTotalFromCartItems(cartItems)} &#8381;</span>
                                <span  style={{ color: "red", marginLeft: "5px", marginRight: "8px"}}>-  {calculateSaleFromCartItems(cartItems, appliedCode)} &#8381;</span>
                                </>)}

                                <span>{calculateFinalFromCartItems(cartItems, appliedCode)} &#8381;</span>
                            </div>
                        </div>
                        {appliedCode?.type === 'delivery' && <div className='cart-total-summary'>
                            <div>Доставка</div>
                            <div><span>0 руб.</span></div>
                        </div>}
                        <div className='cart-promocode-wrap'>
                            <div className='cart-promocode-icon'><Promo /></div>
                            {appliedCode ?
                                <div className='cart-promocode-apply'>
                                    <div>
                                        Применен промокод
                                    </div>
                                    <div className='cart-promocode-apply-icon' onClick={handleInfoCodeClick}>
                                        <span>{appliedCode?.code}</span>
                                        <Question />
                                    </div>
                                </div>
                                : <div onClick={() => setDropdownOpen(true)}
                                 className='cart-promocode'>Применить промокод</div>}


                        </div>
                        <div className="cart-button-wrapper">
                            <Link className="link" to={!!authToken ? '/order-address-select': '/order-user'}>
                                <button disabled={
                                    !cartItems.length ||
                                    (cartItems.length === 1 && cartItems[0].availability === 0)
                                } className="cart-button" onClick={handleOrder}>
                                    Заказать
                                </button>
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="cart-total">
                        <div className='summary'>
                            <h3>Сумма</h3>
                            <div>
                                <span>Общая сумма</span>
                                <span>{calculateTotalFromCartItems(cartItems)} &#8381;</span>
                            </div>
                            {calculateSaleFromCartItems(cartItems, appliedCode) && (
                                <div>
                                    <span>Скидка</span>
                                    <span>{calculateSaleFromCartItems(cartItems, appliedCode)} ₽</span>
                                </div>
                            )}
                             {appliedCode?.type === 'delivery' && <div><span>Доставка</span><span>0 ₽</span></div>}
                           
                            <hr className="hr" />
                            <div className='total'>
                                <span>Итог</span>
                                <span>{calculateFinalFromCartItems(cartItems, appliedCode)} &#8381;</span>
                            </div>
                        </div>
                        <div className='cart-promocode-wrap'>

                            <div className='cart-promocode-icon'><Promo /></div>
                            {appliedCode ?
                                <div className='cart-promocode-apply'>
                                    <div>
                                        Применен промокод
                                    </div>
                                    <div className='cart-promocode-apply-icon' onClick={handleInfoCodeClick}>
                                        <span>{appliedCode?.code}</span>
                                        <Question />
                                    </div>
                                </div>
                                : <div onClick={() => setDropdownOpen(true)}
                                 className='cart-promocode'>Применить промокод</div>}

                        </div>
                        <div className="cart-button-wrapper">
                            <Link className="link" to={!!authToken ? '/order-address-select': '/order-user'}>
                                <button disabled={
                                    !cartItems.length ||
                                    (cartItems.length === 1 && cartItems[0].availability === 0)
                                } className="cart-button" onClick={handleOrder}>
                                    Перейти к оформлению
                                </button>
                            </Link>
                        </div>
                    </div>
                )}
                {notification && (
                    <div className="notification">
                        {notification}
                    </div>
                )}
                <BottomSheet
                    open={dropdownOpen}
                    skipInitialTransition
                    onDismiss={() => {
                        setDropdownOpen(false);
                        if (inputRef.current) {
                            inputRef.current.blur();
                        }
                    }}
                    onSpringStart={()=>{
                        if (dropdownOpen){
                            const input = document.querySelector("input");
                            input.focus();
                        }
                
                    }}
                    style={{ ['--rsbs-overlay-rounded']:0}}
                >
                    <div className="cart-bottom-sheet-container">
                        <div className="cart-bottom-sheet-header-name">
                            Промокод
                        </div>
                        <div className="cart-bottom-sheet-input-code">
                            <label htmlFor="promocode" style={{
                                visibility: errorCode ? 'initial' : 'hidden'
                            }}>{errorMessage || 'Неверный код'}</label>
                            <input
                                ref={inputRef}
                                className={`cart-input ${errorCode ? 'error' : ''}`}
                                type="text"
                                name='promocode'
                                value={code}
                                autoFocus
                                onChange={handleChangeCode}
                                placeholder="Ваш промокод"
                            />
                            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" onClick={handleClickCleanInput}
                                className={`cart-bottom-sheet-close ${errorCode ? 'error' : ''}`}>
                                <path d="M6.40001 18.3069L5.69226 17.5992L11.2923 11.9992L5.69226 6.39916L6.40001 
                                    5.69141L12 11.2914L17.6 5.69141L18.3078 6.39916L12.7078 11.9992L18.3078 17.5992L17.6 
                                    18.3069L12 12.7069L6.40001 18.3069Z" fill="currentColor" />
                            </svg>
                        </div>
                        <div className='cart-bottom-sheet-btn-wrap'>
                            {appliedCode && <div className='cart-bottom-sheet-apply-code'>{appliedCode.message}</div>}
                            <button disabled={code ? false : true} className="cart-button" onClick={checkCode}>
                                Добавить
                            </button>
                        </div>
                    </div>

                </BottomSheet>
                {openModalInfo && <Modal
                    isOpen
                    isCloseButtonOnTop
                    onClose={() => setOpenModalInfo(false)}
                >
                    <div className='modal-info-title'>Промокод {appliedCode?.code}</div>
                    <div className='modal-info-description'>{appliedCode?.message}</div>
                    <div className='modal-info-delete-code' onClick={handleDeleteCode}>
                        <span>Удалить промокод</span>
                    </div>
                </Modal>}
            </main>
            {isOpenModal && <Modal isOpen title="Удалить товар из корзины?"
            onClose={()=> setIsOpenModal(false)}
            footer={<div style={{display:'flex', justifyContent:'flex-end'}}>
            <button style={{}} className='footer-button' onClick={()=> setIsOpenModal(false)}>Отменить</button>
            <button style={{}} className='footer-button' onClick={()=> handleRemoveItem(currentItemId.current)}>Удалить</button>
            </div>}
        />}
        </>
    );
};

export default Cart;